// Program Search Filters /////////////////////////////////////////////////////
//
// This file controls filter behavior for the directory search.
//
// App -> Search Container -> Search Form -> Program Search -> Program Search Filters
///////////////////////////////////////////////////////////////////////////////

// SETUP //////////////////////////////////////////////////////////////////////
// Third-party imports.
import { Row, Col, Input, FormGroup, Label, Collapse, Button, } from 'reactstrap'
import React, { useState, useEffect, } from 'react'
import Select from 'react-select'
import PropTypes from 'prop-types'

// Local imports.
import MultiSelect from './SelectWrapper'
import Slider from '../../jquery-ui/Slider'
import RangeSlider from '../../jquery-ui/RangeSlider'

// Constants.
const noop = () => {}

// HELPER FUNCTIONS ///////////////////////////////////////////////////////////
/**
 * Alphabetically sorts input objects by a "name" value.
 * 
 * @param {*} a 
 * @param {*} b 
 * @returns 
 */
function compareRecords(a, b) {
  return a['name'].localeCompare(b['name'])
}

/**
 * Takes an array of objects with a "name" value, sorting it and removing 
 * any duplicates.
 * 
 * @param {*} inputArray 
 */
function sortAndScrub(inputArray) {
  const uniqArray = Array.from(new Set(inputArray))
  const sortedArray = uniqArray.sort(compareRecords)
  return sortedArray  
}

// MAIN FUNCTION //////////////////////////////////////////////////////////////
const ProgramSearchFilters = ({ 
  counties, 
  programTypes, 
  programLevels, 
  eligibilityRequirements, 
  minimumEducationRequirements, 
  industryTags, 
  schoolNameFilter,
  setSchoolNameFilter,
  countyFilter, 
  setCountyFilter, 
  cityFilter,
  setCityFilter,
  zipCodeFilter,
  setZipCodeFilter,
  programTypeFilter,
  setProgramTypeFilter,
  programLevelFilter,
  setProgramLevelFilter,
  eligibilityRequirementFilter,
  setEligibilityRequirementFilter,
  minimumEducationRequirementFilter,
  setMinimumEducationRequirementFilter,
  minimumAgeFilter,
  setMinimumAgeFilter,
  industryTagFilter,
  setIndustryTagFilter,
  wageMinFilter,
  setWageMinFilter,
  wageMaxFilter,
  setWageMaxFilter,
  radiusFilter,
  setRadiusFilter,
  mobileMapShowing,
  isLargeDisplay = false,
  setBasicFilter,
  setLocationFilter,
  onToggleListOrMap = noop,
}) => {
  const [ isOpen, setIsOpen, ] = useState(false)
  const [ filterText, setFilterText, ] = useState([])

  const wageSliderReset = React.useRef(null)
  const radiusSliderReset = React.useRef(null)

  let filterButtonColSize = 12, doneButtonColSize = 2, mapButtonColSize = 2
  if (!isLargeDisplay) {
    if (isOpen) {
      filterButtonColSize = 3
      doneButtonColSize = 3
      mapButtonColSize = 3
    } else {
      filterButtonColSize = 3
      doneButtonColSize = 3
      mapButtonColSize = 3
    }
  } else {
    if (isOpen) {
      filterButtonColSize = 3
      doneButtonColSize = 3
      mapButtonColSize = 3
    } else {
      filterButtonColSize = 12
      doneButtonColSize = 3
      mapButtonColSize = 3
    }
  }

  // CALLBACK FUNCTIONS ///////////////////////////////////////////////////////
  const subscribeWageSlider = (callback) => {
    wageSliderReset.current = callback
  }

  const subscribeRadiusSlider = (callback) => {
    radiusSliderReset.current = callback
  }

  const resetFilters = React.useCallback(() => {
    if (wageSliderReset != null && wageSliderReset !== undefined && wageSliderReset.current != null && wageSliderReset.current !== undefined){
      wageSliderReset.current()
    }
    if (radiusSliderReset && radiusSliderReset.current){
      radiusSliderReset.current()
    }
    setBasicFilter('')
    setCityFilter('')
    setCountyFilter([])
    setEligibilityRequirementFilter([])
    setIndustryTagFilter([])
    setLocationFilter('')
    setMinimumAgeFilter('')
    setMinimumEducationRequirementFilter([])
    setProgramLevelFilter([])
    setProgramTypeFilter([])
    setRadiusFilter('')
    setSchoolNameFilter('')
    setWageMaxFilter('')
    setWageMinFilter('')
    setZipCodeFilter('')
  }, [ 
    setBasicFilter,
    setCityFilter, 
    setCountyFilter, 
    setEligibilityRequirementFilter, 
    setIndustryTagFilter, 
    setLocationFilter,
    setMinimumAgeFilter, 
    setMinimumEducationRequirementFilter, 
    setProgramLevelFilter, 
    setProgramTypeFilter, 
    setRadiusFilter, 
    setSchoolNameFilter, 
    setWageMaxFilter, 
    setWageMinFilter, 
    setZipCodeFilter, 
  ])

  // USE EFFECTS //////////////////////////////////////////////////////////////
  useEffect(() => {
    const filterText = []
    
    if (!isOpen) {
      if (countyFilter && countyFilter.length > 0) {
        let countyNames = countyFilter.map((item) => { return item.name }).join(', ')
        if (countyFilter.length == 1) {
          filterText.push(`County: ${countyNames}`)
        } else {
          filterText.push(`Counties: ${countyNames}`)
        }
      }
      if (cityFilter) 
        filterText.push(`City: ${cityFilter}`)
      if (wageMaxFilter || wageMinFilter) 
        filterText.push(`Wage: $${wageMinFilter || 0}-$${wageMaxFilter || 50}`)
      if (radiusFilter) 
        filterText.push(`Radius: ${radiusFilter} mile${radiusFilter == 1 ? '' : 's'}`)
      if (zipCodeFilter) 
        filterText.push(`Zip: ${zipCodeFilter}`)
      if (programTypeFilter && programTypeFilter.length > 0) {
        let types = programTypeFilter.map((item) => { return item.name }).join(', ')
        if (programTypeFilter.length == 1) {
          filterText.push(`Program Type: ${types}`)
        } else {
          filterText.push(`Program Types: ${types}`)
        }
      }
      if (programLevelFilter && programLevelFilter.length > 0) {
        let programs = programLevelFilter.map((item) => { return item.name }).join(', ')
        if (programLevelFilter.length == 1) {
          filterText.push(`Program Level: ${programs}`)
        } else {
          filterText.push(`Program Levels: ${programs}`)
        }
      }
      if (eligibilityRequirementFilter && eligibilityRequirementFilter.length > 0) {
        let eligibility = eligibilityRequirementFilter.map((item) => { return item.name }).join(', ')
        filterText.push(`Eligibility: ${eligibility}`)
      }
      if (minimumEducationRequirementFilter && minimumEducationRequirementFilter.length > 0) {
        let minEducation = minimumEducationRequirementFilter.map((item) => { return item.name }).join(', ')
        filterText.push(`Minimum Education: ${minEducation}`)
      }
      if (minimumAgeFilter && minimumAgeFilter !== '') {
        filterText.push(`Minimum Age: ${minimumAgeFilter}`)
      }
      if (industryTagFilter && industryTagFilter.length > 0) {
        let names = industryTagFilter.map((item) => { return item.name }).join(', ')
        if (industryTagFilter.length == 1) {
          filterText.push(`Area of Interest: ${names}`)
        } else {
          filterText.push(`Areas of Interest: ${names}`)
        }
      }
    }
    setFilterText(filterText)
  }, [ cityFilter, countyFilter, eligibilityRequirementFilter, industryTagFilter, isOpen, minimumAgeFilter, minimumEducationRequirementFilter, programLevelFilter, programTypeFilter, radiusFilter, schoolNameFilter, wageMaxFilter, wageMinFilter, zipCodeFilter, ]) 

  // RETURN HTML //////////////////////////////////////////////////////////////
  return <>
    <Row className={'search-inner-row bg-white border-top justify-content-between py-2 pt-3 pb-3'} >
      <Col className={'ml-3 show-cursor-pointer'} onClick={() => setIsOpen(!isOpen)} xs={filterButtonColSize}>
        {
          (filterText && !isOpen)
            ? <div className={'filter-title mr-1'}>Filters:</div>
            : <span className={'filter-title'} title={'Click here to refine your search'}>More Filters...</span>
        }
      </Col>
      {
        isOpen && 
          <Col className={'show-cursor-pointer'} onClick={() => setIsOpen(!isOpen)} xs={doneButtonColSize}>
            <span className={'filter-close'}>Done</span>
          </Col>
      }
      {
        !isLargeDisplay && !isOpen &&
          <Col xs={mapButtonColSize}>
            <Button 
              onClick={onToggleListOrMap} 
              className={'float-right mr-3 d-lg-none p-0 link-button'} 
              name={'show-mobile-map'} 
              id={'btn-show-map'}
              color={'link'}
              style={{ boxShadow: 'none' , textDecoration: 'none' , }}
            >{ mobileMapShowing ? <><i className={'fas fa-list mr-2'}></i>List</> : <><i className={'fas fa-map mr-2'}></i>Map</>}</Button>
          </Col>
      }
    </Row>
    <Row className={'border-top-0'}>
      <ul className={'filters-text'}>{filterText.map( (filter, idx) => {
        return (
          <li key={idx}>
            {filter}
          </li>
        )})}</ul>
    </Row>
    <Collapse isOpen={isOpen} className={'search-inner-row bg-grey-1'}>
      <Row className={'pt-2 pb-3 pl-3 pr-4'}>
        <Col xs={12} md={6}>
          <FormGroup>
            <Label htmlFor={'search-industry'} className={'body-text-bold'}>Area of Interest</Label>
            <MultiSelect
              id={'search-industry'}
              name={'search-industry'}
              options={sortAndScrub(industryTags)}
              optionLabel={'name'}
              optionValue={'id'}
              onChange={(e) => setIndustryTagFilter(e.target.value)}
              value={industryTagFilter}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={6}>
          <FormGroup>
            <Label htmlFor={'search-program-type'} className={'body-text-bold'}>Program Type</Label>
            <MultiSelect
              id={'search-program-type'}
              name={'search-program-type'}
              options={sortAndScrub(programTypes)}
              optionLabel={'name'}
              optionValue={'id'}
              onChange={(e) => setProgramTypeFilter(e.target.value)}
              value={programTypeFilter}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={6}>
          <FormGroup>
            <Label htmlFor={'search-minimum-age'} className={'body-text-bold'}>Your Age</Label>
            <Input
              type={'number'}
              name={'search-minimum-age'}
              id={'search-minimum-age'}
              label={'Age'}
              onChange={(e) => setMinimumAgeFilter(e.target.value)}
              value={minimumAgeFilter}
              min={'10'}
              max={'30'} />
          </FormGroup>
        </Col>
        <Col xs={12} md={6} className={'na-prep na-explore d-none'}>
          <FormGroup>
            <Label htmlFor={'search-eligibility'} className={'body-text-bold'}>Special Eligibility Requirements</Label>
            <MultiSelect
              id={'search-eligibility'}
              name={'search-eligibility'}
              options={eligibilityRequirements}
              optionLabel={'name'}
              optionValue={'id'}
              onChange={(e) => setEligibilityRequirementFilter(e.target.value)}
              value={eligibilityRequirementFilter}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={6}>
          <FormGroup>
            <Label htmlFor={'search-education'} className={'body-text-bold'}>Your Education Level</Label>
            <MultiSelect
              id={'search-education'}
              name={'search-education'}
              options={minimumEducationRequirements}
              optionLabel={'name'}
              optionValue={'id'}
              onChange={(e) => setMinimumEducationRequirementFilter(e.target.value)}
              value={minimumEducationRequirementFilter}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={6} className={'d-none'}>
          <FormGroup>
            <Label htmlFor={'search-counties'} className={'body-text-bold'}>In County</Label>
            <MultiSelect
              id={'search-counties'}
              name={'search-counties'}
              options={counties}
              optionLabel={'name'}
              optionValue={'id'}
              onChange={(e) => setCountyFilter(e.target.value)}
              value={countyFilter}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={6} className={'na-prep na-explore'}>
          <FormGroup>
            <Label htmlFor={'search-program-level'} className={'body-text-bold'}>Desired Education Level</Label>
            <MultiSelect
              id={'search-program-level'}
              name={'search-program-level'}
              options={programLevels}
              optionLabel={'name'}
              optionValue={'id'}
              onChange={(e) => setProgramLevelFilter(e.target.value)}
              value={programLevelFilter}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={6} className={'d-md-block'}>
          <Label htmlFor={'search-wage'} className={'body-text-bold'}>Hourly Wage Range (while in training)</Label>
          <FormGroup>
            {isLargeDisplay ? <RangeSlider
              defaultValues={[ 0, 50, ]}
              min={0}
              max={50}
              step={1}
              handleIdPrefix={'wage-slider'}
              onMinChanged={setWageMinFilter}
              onMaxChanged={setWageMaxFilter}
              filterResetSubscription={subscribeWageSlider}
            /> : <Select options={
              [
                { label: 'All Wages', value: [ 0,50, ], },
                { label: '$0 - $15', value: [ 0,15, ], },
                { label: '$15 - $25', value: [ 15,25, ], },
                { label: '$25 - $35', value: [ 25,35, ], },
                { label: '$35 - $45', value: [ 35,45, ], },
                { label: '$45+', value: [ 45, ], },
              ]
            }
            menuPlacement={'auto'}
            onChange={(option) => {
              setWageMinFilter(option.value[0])
              setWageMaxFilter(option.value[1])
            }}>
            </Select>}
          </FormGroup>
        </Col>
        <Col xs={12} md={6} className={'d-none'}>
          <FormGroup>
            <Label htmlFor={'search-city'} className={'body-text-bold'}>City</Label>
            <Input
              type={'text'} 
              placeholder={'City'} 
              name={'search-city'}
              id={'search-city'}
              value={cityFilter} 
              onChange={e => setCityFilter(e.target.value)} 
              label={'City'}
              className={'occupation-search-input occupation-search-placeholder'}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={6} className={'d-none'}>
          <FormGroup>
            <Label htmlFor={'search-zip'} className={'body-text-bold'}>Zip Code</Label>
            <Input
              type={'text'} 
              placeholder={'Zip'} 
              name={'search-zip'}
              id={'search-zip'}
              value={zipCodeFilter} 
              onChange={e => setZipCodeFilter(e.target.value)} 
              label={'Zip Code'}
              className={'occupation-search-input occupation-search-placeholder'}
            />
          </FormGroup>
        </Col>
        <Col xs={12} md={6} className={'d-none'}> {/*{(userLocation) ? '' : 'd-none'}*/}
          <Label htmlFor={'search-radius'} className={'body-text-bold'}>Radius from Current Location</Label>
          <FormGroup className={'pr-4'}>
            <Slider
              defaultValue={0}
              min={0}
              max={100}
              step={1}
              handleIdPrefix={'radius-slider'}
              onSliderChanged={setRadiusFilter}
              filterResetSubscription={subscribeRadiusSlider}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row className={'filter-border border-bottom pb-2'}>
        <Col xs={6}>
          <Button className={'w-100 h-100 apply-filters'} name={'apply-filters'} id={'btn-apply-filters'}>Apply Filters</Button>
        </Col>
        <Col xs={6}>
          <Button onClick={resetFilters} className={'float-right btn btn-inverted'} name={'clear-filters'} id={'btn-clear-filters'}>Clear Filters</Button>
        </Col>
      </Row>
    </Collapse>
  </>
}

ProgramSearchFilters.propTypes = {
  cityFilter                           : PropTypes.string,
  counties                             : PropTypes.array,
  countyFilter                         : PropTypes.array,
  eligibilityRequirementFilter         : PropTypes.array,
  eligibilityRequirements              : PropTypes.array,
  industryTagFilter                    : PropTypes.array,
  industryTags                         : PropTypes.array,
  isLargeDisplay                       : PropTypes.bool,
  minimumAgeFilter                     : PropTypes.oneOfType([ PropTypes.number, PropTypes.string , ]),
  minimumEducationRequirementFilter    : PropTypes.array,
  minimumEducationRequirements         : PropTypes.array,
  mobileMapShowing                     : PropTypes.bool,
  onToggleListOrMap                    : PropTypes.func,
  programLevelFilter                   : PropTypes.array,
  programLevels                        : PropTypes.array,
  programTypeFilter                    : PropTypes.array,
  programTypes                         : PropTypes.array,
  radiusFilter                         : PropTypes.oneOfType([ PropTypes.number, PropTypes.string , ]),
  schoolNameFilter                     : PropTypes.string,
  setBasicFilter                       : PropTypes.func,
  setCityFilter                        : PropTypes.func,
  setCountyFilter                      : PropTypes.func,
  setEligibilityRequirementFilter      : PropTypes.func,
  setIndustryTagFilter                 : PropTypes.func,
  setLocationFilter                    : PropTypes.func,
  setMinimumAgeFilter                  : PropTypes.func,
  setMinimumEducationRequirementFilter : PropTypes.func,
  setProgramLevelFilter                : PropTypes.func,
  setProgramTypeFilter                 : PropTypes.func,
  setRadiusFilter                      : PropTypes.func,
  setSchoolNameFilter                  : PropTypes.func,
  setWageMaxFilter                     : PropTypes.func,
  setWageMinFilter                     : PropTypes.func,
  setZipCodeFilter                     : PropTypes.func,
  wageMaxFilter                        : PropTypes.oneOfType([ PropTypes.number, PropTypes.string , ]),
  wageMinFilter                        : PropTypes.oneOfType([ PropTypes.number, PropTypes.string , ]),
  zipCodeFilter                        : PropTypes.string,
}


export default ProgramSearchFilters